/**
 * TASK-COMBAT-VIZ-003: Combat Event Handler Implementation
 *
 * Processes combat:events stream with buffering, deduplication, and type-specific callbacks.
 * Maintains rolling event buffer for combat log and analytics.
 */

import type { CombatEvent, UnitMovedEventData, CleanseAppliedEventData } from '@drawn-of-war/shared/types/combat';

/**
 * Event handler options
 */
export interface CombatEventHandlerOptions {
  /** Maximum number of events to keep in buffer (default: 100) */
  bufferSize?: number;
}

/**
 * CombatEventHandler
 *
 * Processes incoming combat events, maintains event buffer, and triggers type-specific callbacks.
 */
export class CombatEventHandler {
  private eventBuffer: CombatEvent[] = [];
  private bufferSize: number;
  private eventSet: Set<string> = new Set();

  // Movement ability callbacks (L3-MOVEMENT-ABILITIES)
  private unitMovedCallbacks: ((data: UnitMovedEventData) => void)[] = [];
  // Cleanse applied callbacks (L4-BATCH1-009)
  private cleanseAppliedCallbacks: ((data: CleanseAppliedEventData) => void)[] = [];

  constructor(options: CombatEventHandlerOptions = {}) {
    this.bufferSize = options.bufferSize ?? 100;
  }

  /**
   * Process incoming combat events
   */
  public processEvents(events: CombatEvent[]): void {
    // Filter out duplicate events
    const newEvents = events.filter(event => {
      const eventKey = this.getEventKey(event);
      if (this.eventSet.has(eventKey)) {
        return false; // Duplicate
      }
      this.eventSet.add(eventKey);
      return true;
    });

    // Add to buffer
    this.eventBuffer.push(...newEvents);

    // Sort by tick to maintain chronological order
    this.eventBuffer.sort((a, b) => a.tick - b.tick);

    // Trim buffer to size limit
    if (this.eventBuffer.length > this.bufferSize) {
      const removed = this.eventBuffer.splice(0, this.eventBuffer.length - this.bufferSize);
      // Remove keys for evicted events
      removed.forEach(event => {
        this.eventSet.delete(this.getEventKey(event));
      });
    }

    // Trigger callbacks for new events
    newEvents.forEach(event => {
      this.triggerCallbacks(event);
    });
  }

  /**
   * Get all recent events
   */
  public getRecentEvents(): CombatEvent[] {
    return [...this.eventBuffer];
  }

  /**
   * Get events by type
   */
  public getEventsByType(eventType: CombatEvent['eventType']): CombatEvent[] {
    return this.eventBuffer.filter(event => event.eventType === eventType);
  }

  /**
   * Get events by tick range (inclusive)
   */
  public getEventsByTickRange(startTick: number, endTick: number): CombatEvent[] {
    return this.eventBuffer.filter(event => event.tick >= startTick && event.tick <= endTick);
  }

  /**
   * Clear all events from buffer
   */
  public clear(): void {
    this.eventBuffer = [];
    this.eventSet.clear();
  }

  /**
   * Register callback for unit_moved events (L3-MOVEMENT-ABILITIES)
   * Called for movement abilities: teleport, dash, charge, leap
   */
  public onUnitMoved(callback: (data: UnitMovedEventData) => void): void {
    this.unitMovedCallbacks.push(callback);
  }

  /**
   * Register callback for cleanse_applied events (L4-BATCH1-009)
   * Called when debuffs are removed from a unit via cleanse ability
   */
  public onCleanseApplied(callback: (data: CleanseAppliedEventData) => void): void {
    this.cleanseAppliedCallbacks.push(callback);
  }

  /**
   * Generate unique key for event deduplication
   */
  private getEventKey(event: CombatEvent): string {
    return `${event.tick}:${event.eventType}:${JSON.stringify(event.data)}`;
  }

  /**
   * Trigger callbacks for event based on type
   */
  private triggerCallbacks(event: CombatEvent): void {
    switch (event.eventType) {
      case 'unit_moved':
        if (this.unitMovedCallbacks.length > 0) {
          const movedData = event.data as UnitMovedEventData;
          this.unitMovedCallbacks.forEach(cb => cb(movedData));
        }
        break;
      case 'cleanse_applied':
        if (this.cleanseAppliedCallbacks.length > 0) {
          const cleanseData = event.data as CleanseAppliedEventData;
          this.cleanseAppliedCallbacks.forEach(cb => cb(cleanseData));
        }
        break;
    }
  }
}
